import { useState, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || '';

async function parseOnServer(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API}/api/parse?type=template`, { method: 'POST', credentials: 'include', body: formData });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Parse failed');
  return data.data;
}

async function saveFinancials(slug, data, sourceFilename) {
  const res = await fetch(`${API}/api/data?resource=financials`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, uploadType: 'template', data, sourceFilename })
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to save');
}

function CompactUpload({ slug, onDataUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  async function handleFile(e) {
    const file = e.target.files[0]; if (!file) return;
    setError(null); setUploading(true);
    try {
      const data = await parseOnServer(file);
      await saveFinancials(slug, data, file.name);
      setDone(true);
      if (onDataUploaded) onDataUploaded({ type: 'template', data });
      setTimeout(() => setDone(false), 3000);
    } catch (err) { setError(err.message); setTimeout(() => setError(null), 4000); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="btn-outline"
        style={{ padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 5, color: done ? 'var(--green)' : error ? 'var(--red)' : 'var(--text-secondary)' }}
        title="Upload KPI template"
      >
        {uploading ? (
          <span style={{ fontSize: 11 }}>Uploading...</span>
        ) : done ? (
          <><svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="2,7 5.5,10.5 12,4"/></svg><span style={{ fontSize: 11 }}>Uploaded</span></>
        ) : error ? (
          <><svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg><span style={{ fontSize: 11 }}>Error</span></>
        ) : (
          <><svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M7 1v8M4 6l3-3 3 3M2 11h10"/></svg><span style={{ fontSize: 11 }}>Upload</span></>
        )}
      </button>
    </div>
  );
}

export default function UploadFinancials({ slug, onDataUploaded, compact }) {
  // Compact mode - inline button for topbar
  if (compact) {
    return <CompactUpload slug={slug} onDataUploaded={onDataUploaded} />;
  }
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  async function handleFile(e) {
    const file = e.target.files[0]; if (!file) return;
    setError(null); setUploading(true);
    try {
      const data = await parseOnServer(file);
      await saveFinancials(slug, data, file.name);
      setUploaded({ name: file.name, data });
      if (onDataUploaded) onDataUploaded({ type: 'template', data });
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeUpload() {
    await fetch(`${API}/api/data?resource=financials&slug=${slug}&uploadType=template`, { method: 'DELETE', credentials: 'include' });
    setUploaded(null);
    if (onDataUploaded) onDataUploaded({ type: 'template', data: null });
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: open ? 'var(--bg-card)' : 'var(--bg)', border: `1px solid ${open ? 'var(--border-strong)' : 'var(--border)'}`, borderRadius: open ? '10px 10px 0 0' : 10, cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: 'var(--text-muted)' }}><path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>Upload KPI template</span>
          {uploaded && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: 'var(--green-bg)', color: 'var(--green)', fontWeight: 600, border: '1px solid rgba(26,122,60,0.15)' }}>Uploaded</span>}
          {uploading && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Processing...</span>}
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.15s' }}>▾</span>
      </button>

      {open && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '14px 16px' }}>
          {error && <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 6, padding: '8px 12px', color: 'var(--red)', fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{error}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
            Upload the SCV Portfolio Upload Template Excel file. Tabs, KPIs, goals, and data all load from this single file.
          </div>
          {uploaded ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--green)', background: 'var(--green-bg)', padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(26,122,60,0.2)' }}>
                ✓ {uploaded.name}
              </div>
              <button onClick={removeUpload} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Remove</button>
            </div>
          ) : (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline" style={{ fontSize: 12 }}>
                {uploading ? 'Processing...' : 'Choose Excel file'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
